import type { FC, HTMLAttributes, ReactNode } from "react";
import { useCallback, useEffect, useRef } from "react";
import type { Placement } from "@react-types/overlays";
import { ChevronSelectorVertical, LogOut01 } from "@untitledui/icons";
import { useTranslation } from "react-i18next";
import { useFocusManager } from "react-aria";
import type { DialogProps as AriaDialogProps } from "react-aria-components";
import { Button as AriaButton, Dialog as AriaDialog, DialogTrigger as AriaDialogTrigger, Popover as AriaPopover } from "react-aria-components";
import { AvatarLabelGroup } from "@/components/base/avatar/avatar-label-group";
import { useBreakpoint } from "@/hooks/use-breakpoint";
import { cx } from "@/utils/cx";

/**
 * Karta účtu v patičce sidebaru + její menu, sdílené s horní lištou
 * (`components/app/AppHeader.tsx`) — obě místa tak nabízejí zaručeně totéž.
 *
 * Oproti UUI předloze bez přepínání účtů a bez avatarů z `untitledui.com`:
 * přihlášený uživatel je právě jeden a externí požadavky z bankovní sítě stejně
 * neprojdou. Volající předává hotový popisek (viz `accountDisplay` v `lib/auth.ts`).
 */
export type NavAccountMenuItem = {
    label: string;
    icon?: FC<{ className?: string }>;
    onClick?: () => void;
};

export const NavAccountMenu = ({
    className,
    onSignOut,
    menuItems = [],
    menuTop,
    ...dialogProps
}: AriaDialogProps & {
    className?: string;
    onSignOut?: () => void;
    menuItems?: NavAccountMenuItem[];
    menuTop?: ReactNode;
}) => {
    const { t } = useTranslation();
    const focusManager = useFocusManager();
    const dialogRef = useRef<HTMLDivElement>(null);

    const onKeyDown = useCallback(
        (e: KeyboardEvent) => {
            switch (e.key) {
                case "ArrowDown":
                    focusManager?.focusNext({ tabbable: true, wrap: true });
                    break;
                case "ArrowUp":
                    focusManager?.focusPrevious({ tabbable: true, wrap: true });
                    break;
            }
        },
        [focusManager],
    );

    useEffect(() => {
        const element = dialogRef.current;
        if (element) {
            element.addEventListener("keydown", onKeyDown);
        }

        return () => {
            if (element) {
                element.removeEventListener("keydown", onKeyDown);
            }
        };
    }, [onKeyDown]);

    return (
        <AriaDialog
            {...dialogProps}
            ref={dialogRef}
            className={cx("w-72 rounded-xl bg-secondary_alt shadow-lg ring ring-secondary_alt outline-hidden", className)}
        >
            <div className="flex flex-col gap-1">
                {menuTop && <div className="rounded-xl bg-primary ring-1 ring-secondary">{menuTop}</div>}
                <div className="rounded-xl bg-primary ring-1 ring-secondary">
                    {menuItems.length > 0 && (
                        <div className="flex flex-col gap-0.5 border-b border-secondary py-1.5">
                            {menuItems.map((item) => (
                                <NavAccountCardMenuItem
                                    key={item.label}
                                    label={item.label}
                                    icon={item.icon}
                                    onClick={item.onClick}
                                />
                            ))}
                        </div>
                    )}
                    <div className="flex flex-col gap-0.5 py-1.5">
                        <NavAccountCardMenuItem label={t("nav.signOut")} icon={LogOut01} onClick={onSignOut} />
                    </div>
                </div>
            </div>
        </AriaDialog>
    );
};

const NavAccountCardMenuItem = ({
    icon: Icon,
    label,
    shortcut,
    ...buttonProps
}: {
    icon?: FC<{ className?: string }>;
    label: string;
    shortcut?: string;
} & HTMLAttributes<HTMLButtonElement>) => {
    return (
        <button {...buttonProps} className={cx("group/item w-full cursor-pointer px-1.5 focus:outline-hidden", buttonProps.className)}>
            <div
                className={cx(
                    "flex w-full items-center justify-between gap-3 rounded-md p-2 group-hover/item:bg-primary_hover",
                    // Focus styles.
                    "outline-focus-ring group-focus-visible/item:outline-2 group-focus-visible/item:outline-offset-2",
                )}
            >
                <div className="flex gap-2 text-sm font-semibold text-secondary group-hover/item:text-secondary_hover">
                    {Icon && <Icon className="size-5 text-fg-quaternary group-hover/item:text-fg-quaternary_hover" />} {label}
                </div>

                {shortcut && (
                    <kbd className="flex rounded px-1 py-px font-body text-xs font-medium text-tertiary ring-1 ring-secondary ring-inset">{shortcut}</kbd>
                )}
            </div>
        </button>
    );
};

export const NavAccountCard = ({
    popoverPlacement,
    title,
    subtitle,
    initials,
    avatarRounded,
    onSignOut,
    menuItems,
    menuTop,
}: {
    popoverPlacement?: Placement;
    /** Jméno uživatele (displayName, jinak username). */
    title: string;
    /** Role uživatele. */
    subtitle: string;
    initials: string;
    avatarRounded?: boolean;
    onSignOut?: () => void;
    menuItems?: NavAccountMenuItem[];
    menuTop?: ReactNode;
}) => {
    const triggerRef = useRef<HTMLDivElement>(null);
    const isDesktop = useBreakpoint("lg");

    return (
        <div ref={triggerRef} className="relative flex items-center gap-3 rounded-xl p-3 ring-1 ring-secondary ring-inset">
            <AvatarLabelGroup size="md" initials={initials} title={title} subtitle={subtitle} rounded={avatarRounded} />

            <AriaDialogTrigger>
                <AriaButton className="absolute top-2 right-2 flex cursor-pointer items-center justify-center rounded-md p-1.5 text-fg-quaternary outline-focus-ring transition duration-100 ease-linear hover:bg-primary_hover hover:text-fg-quaternary_hover focus-visible:outline-2 focus-visible:outline-offset-2 pressed:bg-primary_hover pressed:text-fg-quaternary_hover">
                    <ChevronSelectorVertical className="size-4 shrink-0 stroke-[2.25px]" />
                </AriaButton>
                <AriaPopover
                    placement={popoverPlacement ?? (isDesktop ? "right bottom" : "top right")}
                    triggerRef={triggerRef}
                    offset={8}
                    className={({ isEntering, isExiting }) =>
                        cx(
                            "origin-(--trigger-anchor-point) will-change-transform",
                            isEntering &&
                                "duration-150 ease-out animate-in fade-in placement-right:slide-in-from-left-0.5 placement-top:slide-in-from-bottom-0.5 placement-bottom:slide-in-from-top-0.5",
                            isExiting &&
                                "duration-100 ease-in animate-out fade-out placement-right:slide-out-to-left-0.5 placement-top:slide-out-to-bottom-0.5 placement-bottom:slide-out-to-top-0.5",
                        )
                    }
                >
                    <NavAccountMenu onSignOut={onSignOut} menuItems={menuItems} menuTop={menuTop} />
                </AriaPopover>
            </AriaDialogTrigger>
        </div>
    );
};
